/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomIconButton } from '@opengeoweb/shared';
import { ViewSettingsIcon } from '@opengeoweb/theme';
import { OverlayPluginMenuList } from './OverlayPluginMenuList';
import { getOverlayPlugins, pluginsActions } from '../../store';
import type { StoreWithPlugins } from '../../store';
import { usePluginsTranslation } from '../../utils/i18n';

interface OverlayPluginMenuButtonProps {
  panelId: string;
}

export const OverlayPluginMenuButton = ({
  panelId,
}: OverlayPluginMenuButtonProps): React.ReactElement => {
  const { t } = usePluginsTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);
  const overlayPlugins = useSelector((state: StoreWithPlugins) =>
    getOverlayPlugins(state, panelId),
  );

  const toggleMenu = (): void => {
    setOpen((prev) => !prev);
  };

  const handlePluginToggle = (pluginId: string, isActive: boolean): void => {
    const toggleAction = isActive
      ? pluginsActions.showOverlayPlugin
      : pluginsActions.removeOverlayPlugin;
    dispatch(toggleAction({ panelId, pluginId }));
  };

  const activePluginIds = overlayPlugins.map((plugin) => plugin.pluginId);

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <CustomIconButton
        data-testid="overlay-plugin-menu-button"
        onClick={toggleMenu}
        tooltipTitle={
          !open ? t('open-overlay-plugin-menu') : t('close-overlay-plugin-menu')
        }
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ViewSettingsIcon />
        </Box>
      </CustomIconButton>

      {open ? (
        <Box sx={{ position: 'absolute', top: '120%', right: 0, zIndex: 1400 }}>
          <OverlayPluginMenuList
            activePluginIds={activePluginIds}
            onPluginToggle={handlePluginToggle}
          />
        </Box>
      ) : null}
    </Box>
  );
};
